import { Box, Divider, Heading, SimpleGrid } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { FormikInput, FormikDatepicker } from 'components/Formik';
import Title from 'components/Title';
import { MONTH_DAY_YEAR_DASH_FORMAT, MONTH_DAY_YEAR_TIME_FORMAT } from 'global/appConstants';
import { routePaths } from 'global/routePaths';
import { ISaveEvvEmployeeData, useSaveEvvEmployee } from 'hooks/evv/useSaveEvvEmployee';
import { useCompanyInfo } from 'store/useUserStore';

import { initialEmployeeFormData, employeeFormValidationSchema } from './schema';

interface IEmployeeFormProps {
  formData?: typeof initialEmployeeFormData;
}

const EmployeeForm: React.FC<IEmployeeFormProps> = ({ formData = initialEmployeeFormData }) => {
  const { t } = useTranslation(['evv', 'common']);

  const navigate = useNavigate();

  const { status, mutateAsync } = useSaveEvvEmployee();

  const companyInfo: any = useCompanyInfo();

  const handleFormSubmit = async (values: typeof initialEmployeeFormData) => {
    const postData: ISaveEvvEmployeeData = {
      ...values,
      hiring_date: values.hiring_date ? format(new Date(values.hiring_date), MONTH_DAY_YEAR_DASH_FORMAT) : null,
      termination_date: values.termination_date
        ? format(new Date(values.termination_date), MONTH_DAY_YEAR_DASH_FORMAT)
        : null,
      shift_start_time: values.shift_start_time || null,
      shift_end_time: values.shift_end_time || null,
    };

    mutateAsync(postData).then(data => {
      if (!data.payload) return;

      navigate({
        pathname: routePaths.evv.employees,
      });
    });
  };

  return (
    <Box>
      <Title>{formData.id ? t('evv:updateEvvEmployee') : t('evv:addEvvEmployee')}</Title>
      <Divider mt="6" />
      <Box mt="10" maxWidth="1000px" mx="auto">
        <Formik
          initialValues={formData}
          enableReinitialize
          validationSchema={employeeFormValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {({ values }) => (
            <Form noValidate>
              <Heading size="sm" as="h4">
                {t('evv:personalDetails')}
              </Heading>
              <Divider borderStyle="dashed" my={4} borderColor="gray.400" />
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10" rowGap="0" mb="6">
                <FormikInput
                  type="text"
                  name="first_name"
                  label={t('common:firstName')}
                  formControlProps={{ isRequired: true }}
                />

                <FormikInput type="text" name="middle_name" label={t('evv:middleName')} />
                <FormikInput
                  type="text"
                  name="last_name"
                  label={t('evv:lastName')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput
                  type="text"
                  name="employee_custom_id"
                  label={t('evv:employeeCustomId')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput
                  type="text"
                  name="email"
                  label={t('common:emailAddress')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput
                  type="text"
                  name="phone"
                  label={t('common:phoneNumber')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput type="text" name="position" label={t('common:position')} />
                <FormikInput type="password" name="ssn" label={t('evv:ssn')} formControlProps={{ isRequired: true }} />
              </SimpleGrid>
              <Heading size="sm" as="h4">
                {t('evv:locationDetails')}
              </Heading>
              <Divider borderStyle="dashed" my={4} borderColor="gray.400" />
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10" rowGap="0" mb="6">
                <FormikInput
                  type="text"
                  name="address_line_1"
                  label={t('evv:addressLine1')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput type="text" name="address_line_2" label={t('evv:addressLine2')} />
                <FormikInput type="text" name="city" label={t('common:city')} formControlProps={{ isRequired: true }} />
                <FormikInput
                  type="text"
                  name="state"
                  label={t('common:state')}
                  formControlProps={{ isRequired: true }}
                />
                <FormikInput
                  type="text"
                  name="zip"
                  label={t('common:zipCode')}
                  formControlProps={{ isRequired: true }}
                />
              </SimpleGrid>
              <Heading size="sm" as="h4">
                {t('evv:additionalDetails')}
              </Heading>
              <Divider borderStyle="dashed" my={4} borderColor="gray.400" />
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10" rowGap="0" mb="6">
                <FormikDatepicker
                  label={t('evv:hiringDate')}
                  name="hiring_date"
                  datepickerProps={{
                    showTimeInput: true,
                    popperPlacement: 'top',
                    dateFormat: companyInfo?.companyDateFormat + ' hh:mm a' || MONTH_DAY_YEAR_TIME_FORMAT,
                    startDate: (values.hiring_date as Date) || null,
                    endDate: (values.termination_date as Date) || null,
                    maxDate: (values.termination_date as Date) || null,
                    shouldCloseOnSelect: false,
                  }}
                />
                <FormikDatepicker
                  label={t('evv:terminationDate')}
                  name="termination_date"
                  datepickerProps={{
                    showTimeInput: true,
                    popperPlacement: 'top',
                    dateFormat: companyInfo?.companyDateFormat + ' hh:mm a' || MONTH_DAY_YEAR_TIME_FORMAT,
                    startDate: (values.hiring_date as Date) || null,
                    endDate: (values.termination_date as Date) || null,
                    minDate: (values.hiring_date as Date) || null,
                    shouldCloseOnSelect: false,
                  }}
                />
                <FormikInput type="time" name="shift_start_time" label={t('evv:shiftStartTime')} />
                <FormikInput type="time" name="shift_end_time" label={t('evv:shiftEndTime')} />
              </SimpleGrid>
              <Button type="submit" colorScheme="secondary" isLoading={status === 'loading'}>
                {formData?.id ? t('evv:updateEvvEmployee') : t('evv:addEvvEmployee')}
              </Button>
              <Button
                bg="gray.600"
                _hover={{ bg: 'gray.700' }}
                _active={{ bg: 'gray.700' }}
                ml="5"
                onClick={() => navigate(-1)}
              >
                {t('common:cancel')}
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default EmployeeForm;
